import { getAuthHandle } from '../auth/authBridge'

const BASE = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'

/**
 * An RFC 9457 problem document from the API, or a synthesised equivalent for
 * transport failures. Every screen that renders an error renders one of these.
 */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly detail: string
  readonly requestId: string | null
  readonly errors: string[]

  constructor(init: {
    status: number
    code: string
    detail: string
    requestId?: string | null
    errors?: string[]
  }) {
    super(init.detail)
    this.name = 'ApiError'
    this.status = init.status
    this.code = init.code
    this.detail = init.detail
    this.requestId = init.requestId ?? null
    this.errors = init.errors ?? []
  }

  /** The draft was edited in another tab. Offer a reload, never overwrite. */
  get isStaleDraft(): boolean {
    return this.status === 409 && this.code === 'stale_draft'
  }

  get isOffline(): boolean {
    return this.status === 0
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  /** Extra headers, e.g. `If-Match` for optimistic concurrency on the draft. */
  headers?: Record<string, string>
  signal?: AbortSignal
  /** Internal: prevents an infinite refresh loop. */
  _retried?: boolean
}

function joinPath(path: string): string {
  if (path.startsWith('http') || path.startsWith('/api/')) return path
  return `${BASE}${path.startsWith('/') ? path : `/${path}`}`
}

async function problemFrom(response: Response): Promise<ApiError> {
  let detail = response.statusText || 'Something went wrong'
  let code = `http_${response.status}`
  let errors: string[] = []
  try {
    const body = await response.json()
    if (body && typeof body === 'object') {
      if (typeof body.detail === 'string') detail = body.detail
      if (typeof body.code === 'string') code = body.code
      if (Array.isArray(body.errors)) {
        errors = body.errors.map((entry: unknown) =>
          typeof entry === 'string'
            ? entry
            : typeof entry === 'object' && entry !== null && 'msg' in entry
              ? String((entry as { msg: unknown }).msg)
              : JSON.stringify(entry),
        )
      }
    }
  } catch {
    /* not JSON — the status line is all we have */
  }
  return new ApiError({
    status: response.status,
    code,
    detail,
    requestId: response.headers.get('X-Request-ID'),
    errors,
  })
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const auth = getAuthHandle()
  const authorization = await auth.getAuthorization()

  const headers: Record<string, string> = { Accept: 'application/json', ...options.headers }
  if (authorization) headers.Authorization = authorization
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'

  let response: Response
  try {
    response = await fetch(joinPath(path), {
      method: options.method ?? 'GET',
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
      credentials: 'omit',
    })
  } catch (cause) {
    if (options.signal?.aborted) throw cause
    throw new ApiError({
      status: 0,
      code: 'network_error',
      detail: "We couldn't reach the server. Check your connection and try again.",
    })
  }

  // One retry, and only one: refresh the credential and replay. If it fails
  // again the credential is genuinely dead, so hand over to the login flow
  // rather than looping.
  if (response.status === 401 && !options._retried) {
    await auth.refresh()
    return send(path, { ...options, _retried: true })
  }
  if (response.status === 401) {
    auth.onUnauthorized()
  }

  return response
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options)
  if (!response.ok) throw await problemFrom(response)
  if (response.status === 204) return undefined as T
  const text = await response.text()
  return (text ? JSON.parse(text) : undefined) as T
}

/**
 * Binary fetch for PDFs.
 *
 * An `<iframe src="/api/v1/documents/…">` cannot carry an `Authorization`
 * header, and the document endpoints require one in both auth modes. So we
 * fetch the bytes through the same authenticated client and hand the iframe a
 * blob URL instead. Callers must revoke the URL when they unmount.
 */
export async function apiBlob(
  path: string,
  options: { signal?: AbortSignal; method?: 'GET' | 'POST'; body?: unknown } = {},
): Promise<Blob> {
  // POST is here for the admin template preview, which renders an *unsaved*
  // draft: the thing being rendered only exists in the request body, so there
  // is no id to GET.
  const response = await send(path, {
    method: options.method ?? 'GET',
    body: options.body,
    signal: options.signal,
  })
  if (!response.ok) throw await problemFrom(response)
  return response.blob()
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'PATCH', body }),
  del: <T>(path: string, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'DELETE' }),
}
